import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { RoundsController } from '@/pages/rounds/useRoundsController';
import type { RoundBye } from '@swiss-magic/shared-types';
import { getSecuredPlayerIds } from '@/lib/api';
import { GeneratePairingsByesDialog } from './GeneratePairingsByesDialog';

export function GenerateRoundButtons({ ctrl }: { ctrl: RoundsController }) {
  const { t } = useTranslation('director');
  const {
    tournament,
    rounds,
    players,
    generating,
    godMode,
    isRoundRobin,
    canGenerate,
    hasIncompleteRounds,
    handleGenerate,
    handleGenerateAllRounds,
    handleCreateManualRound,
  } = ctrl;
  const [byesDialogOpen, setByesDialogOpen] = useState(false);
  // Story 105 — full-point byes pre-selected for players who have secured a
  // TOP X place (KO qualifier mode). Fetched fresh each time the dialog opens.
  const [securedPlayerIds, setSecuredPlayerIds] = useState<string[]>([]);
  // Post-Open feedback: the secured check can take seconds — the dialog
  // shows a progress indicator while this is true so a fast director knows
  // proposals are still coming. Only relevant in KO qualifier mode.
  const [securedPending, setSecuredPending] = useState(false);
  const securedRelevant =
    (tournament?.system === '3ko' || tournament?.system === '5ko') &&
    (tournament?.koFinishTop ?? 1) > 1;

  async function openByesDialog() {
    setSecuredPlayerIds([]);
    setByesDialogOpen(true);
    if (tournament?.id && securedRelevant) {
      setSecuredPending(true);
      try {
        const { securedPlayerIds: ids } = await getSecuredPlayerIds(tournament.id);
        setSecuredPlayerIds(ids);
      } catch {
        // Non-fatal: the dialog still works without pre-selection.
      } finally {
        setSecuredPending(false);
      }
    }
  }

  const nextRoundLabel = generating
    ? t('rounds_page.buttons.generating')
    : t('rounds_page.buttons.generate_round_n', { round: rounds.length + 1 });

  if (canGenerate && isRoundRobin && rounds.length === 0) {
    return (
      <div className="mb-6 flex items-center gap-2">
        <Button
          data-testid="generate-all-rounds-button"
          onClick={handleGenerateAllRounds}
          disabled={generating}
        >
          {generating
            ? t('rounds_page.buttons.generating')
            : t('rounds_page.buttons.generate_all_rounds')}
        </Button>
      </div>
    );
  }

  // Non-round-robin: either a normal generate (canGenerate) or a god-mode
  // force-generate past incomplete rounds. Both may also show a manual-round
  // escape hatch in god mode.
  const forceIncomplete =
    godMode && !!hasIncompleteRounds && !canGenerate && !isRoundRobin;
  const showNormal = canGenerate && !isRoundRobin;
  // Polish Open 2026 incident: without god mode, incomplete earlier rounds
  // must block generation VISIBLY — a disabled button + badge + a hint naming
  // the rounds to finish, instead of silently hiding (or worse, allowing) it.
  const blockedIncomplete =
    !godMode && !!hasIncompleteRounds && !showNormal && !isRoundRobin;

  if (!showNormal && !forceIncomplete && !blockedIncomplete) return null;

  if (blockedIncomplete) {
    const incompleteNumbers = rounds
      .filter((r) => r.status === 'published' || r.status === 'paired')
      .map((r) => r.roundNumber)
      .sort((a, b) => a - b)
      .join(', ');
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <Button data-testid="generate-pairings-button" disabled variant="outline">
            {nextRoundLabel}
            <Badge variant="destructive" className="ml-2 text-xs">
              {t('rounds_page.incomplete_rounds_badge')}
            </Badge>
          </Button>
        </div>
        <p
          data-testid="incomplete-rounds-hint"
          className="mt-1 text-xs text-muted-foreground"
        >
          {t('rounds_page.incomplete_rounds_hint', { rounds: incompleteNumbers })}
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 flex items-center gap-2">
      <Button
        data-testid="generate-pairings-button"
        onClick={() => void openByesDialog()}
        disabled={generating}
        variant={forceIncomplete ? 'outline' : 'default'}
      >
        {nextRoundLabel}
        {forceIncomplete && (
          <Badge variant="destructive" className="ml-2 text-xs">
            {t('rounds_page.incomplete_rounds_badge')}
          </Badge>
        )}
      </Button>
      <GeneratePairingsByesDialog
        open={byesDialogOpen}
        roundNumber={rounds.length + 1}
        players={players.filter((p) => p.status === 'accepted')}
        securedPlayerIds={securedPlayerIds}
        securedPending={securedPending}
        generating={generating}
        onCancel={() => setByesDialogOpen(false)}
        onConfirm={(byes: RoundBye[]) => {
          setByesDialogOpen(false);
          void handleGenerate(forceIncomplete, byes);
        }}
      />
      {godMode && (
        <Button
          variant="outline"
          data-testid="create-manual-round-button"
          onClick={handleCreateManualRound}
          disabled={generating}
        >
          <Users className="mr-1 h-4 w-4" />
          {t('rounds_page.buttons.create_manual_round')}
        </Button>
      )}
    </div>
  );
}
